import { useState, useEffect } from 'react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs'
import { UserPlus, Trash2, X, Copy, RefreshCw, Check } from 'lucide-react'

// Base URL of the end-user app where invites are accepted (/invite/:token).
// In production this is the org's subdomain; in dev it defaults to the app port.
const APP_BASE_URL = import.meta.env.VITE_APP_URL || 'http://localhost:5173'
import { adminApi } from '@/lib/api'
import { useOrgContext } from '@/contexts/OrgContext'
import { OrgMember, OrgInvite } from '@/types/organization'

const roleBadge = (role: string) => {
  switch (role) {
    case 'Owner':
      return <Badge className="bg-purple-500/10 text-purple-500 border-purple-500/20">Owner</Badge>
    case 'Admin':
      return <Badge className="bg-blue-500/10 text-blue-500 border-blue-500/20">Admin</Badge>
    case 'Member':
      return <Badge variant="secondary">Member</Badge>
    case 'Guest':
      return <Badge variant="outline">Guest</Badge>
    default:
      return <Badge variant="secondary">{role}</Badge>
  }
}

export function MembersPage() {
  const { selectedOrg, hasOrgContext, orgName } = useOrgContext()
  const [members, setMembers] = useState<OrgMember[]>([])
  const [invites, setInvites] = useState<OrgInvite[]>([])
  const [copiedToken, setCopiedToken] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [inviteOpen, setInviteOpen] = useState(false)
  const [inviting, setInviting] = useState(false)
  const [newInvite, setNewInvite] = useState({ email: '', role: 'Member' })
  const [confirmRemove, setConfirmRemove] = useState<OrgMember | null>(null)

  const fetchMembers = async () => {
    // hasOrgContext, not selectedOrg: on a dedicated tenant the org is implicit
    // and there is nothing to select, so gating on the selection left this page
    // permanently empty for the tenant's own admin.
    if (!hasOrgContext) return
    try {
      setLoading(true)
      const [membersData, invitesData] = await Promise.all([
        adminApi.getOrgMembers(),
        adminApi.getInvites('pending'),
      ])
      setMembers(membersData.members || membersData || [])
      setInvites(invitesData.invites || [])
    } catch (err) {
      console.error('Failed to fetch members:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { fetchMembers() }, [selectedOrg?.slug, hasOrgContext])

  const handleRoleChange = async (userId: string, role: string) => {
    try {
      await adminApi.updateMemberRole(userId, role)
      fetchMembers()
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to update role')
    }
  }

  const handleRemove = async (member: OrgMember) => {
    try {
      await adminApi.removeMember(member.id)
      setConfirmRemove(null)
      fetchMembers()
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to remove member')
    }
  }

  const handleInvite = async () => {
    if (!newInvite.email) return
    setInviting(true)
    try {
      await adminApi.createInvite(newInvite.email, newInvite.role)
      setInviteOpen(false)
      setNewInvite({ email: '', role: 'Member' })
      fetchMembers()
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to send invite')
    } finally {
      setInviting(false)
    }
  }

  const handleRevoke = async (inviteId: string) => {
    try {
      await adminApi.revokeInvite(inviteId)
      fetchMembers()
    } catch (err) {
      console.error('Failed to revoke invite:', err)
    }
  }

  const handleCopyLink = async (token: string) => {
    const link = `${APP_BASE_URL}/invite/${token}`
    try {
      await navigator.clipboard.writeText(link)
      setCopiedToken(token)
      setTimeout(() => setCopiedToken(null), 1500)
    } catch {
      // Clipboard may be blocked; surface the link so it can be copied manually.
      window.prompt('Copy this invite link:', link)
    }
  }

  const handleResend = async (inviteId: string) => {
    try {
      // Regenerates the token + extends expiry; copy the fresh link (no email yet).
      const { invite } = await adminApi.resendInvite(inviteId)
      if (invite?.token) await handleCopyLink(invite.token)
      fetchMembers()
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to resend invite')
    }
  }

  const handleAcceptInvite = async (inviteId: string) => {
    try {
      await adminApi.acceptInviteAdmin(inviteId)
      fetchMembers()
    } catch (err: any) {
      alert(err.response?.data?.error || 'Failed to accept invite')
    }
  }

  if (!hasOrgContext) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">Select an organization first from the Organizations page or the header selector.</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <UserPlus className="h-8 w-8 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">Members</h1>
            <p className="text-sm text-muted-foreground">{orgName}</p>
          </div>
        </div>
        <Button onClick={() => setInviteOpen(true)}>
          <UserPlus className="mr-2 h-4 w-4" />
          Invite Member
        </Button>
      </div>

      <Tabs defaultValue="members">
        <TabsList>
          <TabsTrigger value="members">Members ({members.length})</TabsTrigger>
          <TabsTrigger value="invites">Pending Invites ({invites.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="members" className="mt-4">
          <Card>
            <CardContent className="pt-6">
              {loading ? (
                <div className="text-center py-8 text-muted-foreground">Loading...</div>
              ) : members.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">No members found</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Last Active</TableHead>
                      <TableHead>Joined</TableHead>
                      <TableHead className="w-[50px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {members.map(member => (
                      <TableRow key={member.id}>
                        <TableCell className="font-medium">
                          {[member.firstName, member.lastName].filter(Boolean).join(' ') || '-'}
                        </TableCell>
                        <TableCell>{member.email}</TableCell>
                        <TableCell>
                          {member.role === 'Owner' ? (
                            roleBadge('Owner')
                          ) : (
                            <Select
                              value={member.role}
                              onValueChange={role => handleRoleChange(member.id, role)}
                            >
                              <SelectTrigger className="w-[120px] h-8">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Admin">Admin</SelectItem>
                                <SelectItem value="Member">Member</SelectItem>
                                <SelectItem value="Guest">Guest</SelectItem>
                              </SelectContent>
                            </Select>
                          )}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {member.lastActive ? new Date(member.lastActive).toLocaleDateString() : '-'}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {new Date(member.createdAt).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          {member.role !== 'Owner' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setConfirmRemove(member)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="invites" className="mt-4">
          <Card>
            <CardContent className="pt-6">
              {invites.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">No pending invites</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Invited</TableHead>
                      <TableHead>Expires</TableHead>
                      <TableHead className="w-[50px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invites.map(invite => (
                      <TableRow key={invite.id}>
                        <TableCell>{invite.email}</TableCell>
                        <TableCell>{roleBadge(invite.role)}</TableCell>
                        <TableCell className="text-muted-foreground">
                          {new Date(invite.createdAt).toLocaleDateString()}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {new Date(invite.expiresAt).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="sm" title="Copy invite link" onClick={() => handleCopyLink(invite.token)}>
                              {copiedToken === invite.token
                                ? <Check className="h-4 w-4 text-green-600" />
                                : <Copy className="h-4 w-4" />}
                            </Button>
                            <Button variant="ghost" size="sm" title="Resend (regenerate link + extend expiry)" onClick={() => handleResend(invite.id)}>
                              <RefreshCw className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" title="Accept on behalf (add as member now)" onClick={() => handleAcceptInvite(invite.id)}>
                              <Check className="h-4 w-4 text-green-600" />
                            </Button>
                            <Button variant="ghost" size="sm" title="Revoke invite" onClick={() => handleRevoke(invite.id)}>
                              <X className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Invite Dialog */}
      <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite Member</DialogTitle>
            <DialogDescription>Send an invitation to join {orgName}.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                placeholder="user@example.com"
                value={newInvite.email}
                onChange={e => setNewInvite(prev => ({ ...prev, email: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={newInvite.role} onValueChange={v => setNewInvite(prev => ({ ...prev, role: v }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Admin">Admin</SelectItem>
                  <SelectItem value="Member">Member</SelectItem>
                  <SelectItem value="Guest">Guest</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteOpen(false)}>Cancel</Button>
            <Button onClick={handleInvite} disabled={inviting || !newInvite.email}>
              {inviting ? 'Sending...' : 'Send Invite'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Remove Confirmation Dialog */}
      <Dialog open={!!confirmRemove} onOpenChange={() => setConfirmRemove(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Member</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove {confirmRemove?.email} from {orgName}? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmRemove(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => confirmRemove && handleRemove(confirmRemove)}>
              Remove
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
